import { PlatformBanner } from '@/features/billing/PlatformBanner';
import { BillingBanner } from '@/features/billing/BillingBanner';
import { SettingsReturnBar, useBackClosesDrawer } from '@/lib/origin';
import { FeedbackDialog } from '@/features/feedback/Feedback';
import { NewRoomModal } from '@/features/rooms/NewRoomModal';
import { lazy, Suspense, useState } from 'react';
import { useMedia, useNarrow } from '@/lib/useMedia';
import { Outlet, useLocation } from 'react-router-dom';
import { RealtimeBridge } from '@/lib/RealtimeBridge';
import { useUi } from '@/lib/store';
import clsx from 'clsx';
import { useAuth } from '@/lib/auth';
import { useSidebarPin } from '@/lib/sidebarPin';
import { NavAreaTracker } from '@/lib/navArea';
import { ModuleRoute, useModuleOn } from '@/lib/modules';
import { Sidebar, SidebarRail } from './Sidebar';
import { RailFlyout } from './RailFlyout';
import { TopBar } from './TopBar';
import { TaskPanel } from '@/features/projects/TaskPanel';
import { FloatingThreadPanel } from '@/features/threads/FloatingThreadPanel';
import { IncomingCallModal } from '@/features/calls/IncomingCallModal';
import { AssistantPanel } from '@/features/assistant/Assistant';
import { PersonDrawer } from '@/features/people/PersonDrawer';
import { CommandPalette } from './CommandPalette';
import { MobileTabBar, MoreSheet } from './MobileNav';
import { useHelpShortcut } from '@/components/help/HelpButton';
import { useMobileLayout } from '@/mobile/layout';
import { MobileShell } from '@/mobile/MobileShell';

// LiveKit is ~700 kB; only load it when a call starts.
const CallRoom = lazy(() => import('@/features/calls/CallRoom'));
// Help Centre side panel (the "?" button / ? key); its content and the guide load on first open.
const HelpPanel = lazy(() => import('@/features/help/HelpPanel'));

/** Phase 0: small touch screens get the phone shell (role-shaped tabs) unless "Desktop site" is on. */
export function AppShell() {
  return useMobileLayout() ? <MobileShell /> : <DesktopShell />;
}

function DesktopShell() {
  const taskPanelId = useUi(s => s.taskPanelId);
  const openTask = useUi(s => s.openTask);
  useBackClosesDrawer(Boolean(taskPanelId), () => openTask(null));
  const activeCall = useUi(s => s.activeCall);
  const assistantOpen = useUi(s => s.assistantOpen);
  const assistantOn = useModuleOn('assistant');
  const helpOpen = useUi(s => s.helpPanel !== null);
  useHelpShortcut();
  const { userId } = useAuth();
  const { pinned, setPinned, peek, setPeek } = useSidebarPin(userId);
  // Phones (< 768px): no sidebar or rail; a bottom tab bar with a "More" sheet instead (UI review High #5).
  const narrow = useNarrow();
  const [more, setMore] = useState(false);
  // UI review 2.4: the rota needs the width, so below 1440px Schedule starts with the sidebar collapsed to the rail.
  // Session-only: pinning it here keeps it open for this visit without changing the saved preference.
  const { pathname } = useLocation();
  const wide = useMedia('(min-width: 1440px)');
  const [keepOpen, setKeepOpen] = useState(false);
  const autoCollapse = pathname.startsWith('/workforce/schedule') && !wide && !keepOpen;
  const showPinned = pinned && !autoCollapse;
  const pin = () => { if (pinned) setKeepOpen(true); else setPinned(true); };
  return (
    <div className={clsx('flex h-full overflow-hidden', narrow && 'flex-col')}>
      <a href="#main" className="sr-only z-[70] rounded bg-brand-yellow px-3 py-2 text-sm font-medium text-on-accent-strong focus:not-sr-only focus:fixed focus:left-2 focus:top-2">Skip to content</a>
      <RealtimeBridge />
      <NavAreaTracker />
      {narrow ? null : showPinned ? <Sidebar pinned onTogglePin={() => { setKeepOpen(false); setPinned(false); }} /> : (
        // Unpinned: the icon rail holds the space and never moves; the area pages slide out to its right (t671u).
        <RailFlyout open={peek} setOpen={setPeek} rail={<SidebarRail onExpand={() => setPeek(true)} />}
          panel={<Sidebar pinned={false} flyout onTogglePin={pin} />} />
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <TopBar />
        <PlatformBanner />
        <BillingBanner />
        <SettingsReturnBar />
        <main id="main" tabIndex={-1} className="relative flex min-h-0 flex-1 outline-none">
          <div className="min-w-0 flex-1 overflow-hidden"><ModuleRoute><Outlet /></ModuleRoute></div>
          <FloatingThreadPanel />
          {taskPanelId && <TaskPanel taskId={taskPanelId} />}
          {assistantOpen && assistantOn && <AssistantPanel />}
          {helpOpen && <Suspense fallback={null}><HelpPanel /></Suspense>}
        </main>
      </div>
      {narrow && <MobileTabBar onMore={() => setMore(v => !v)} moreOpen={more} />}
      {narrow && <MoreSheet open={more} onClose={() => setMore(false)} />}
      <PersonDrawer />
      <FeedbackDialog />
      <CommandPalette />
      {activeCall && <Suspense fallback={null}><CallRoom /></Suspense>}
      <IncomingCallModal />
      <NewRoomModal />
    </div>
  );
}
